import React from "react";
import { toast } from "react-toastify";

import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  ProductSubstitutionStatus,
  useSubstitutionsApi,
  IProductSubstitution
} from "src/modules/substitutions/api/substitutions.api";

import { SubstitutionTab } from "./SubstitutionTab";
import { AffectedOrdersTab } from "./AffectedOrdersTab";

export interface ProductSubstitutionPageProps {
  substitutionId: string;
}

export const ProductSubstitutionPage: React.ReactFCWithChildren<PageProps<unknown, ProductSubstitutionPageProps>> = props => {
  const substitutionId = React.useMemo(() => parseInt(props.match?.params.substitutionId as string, 10), [ props.match?.params.substitutionId ]);
  const [ getSubstitutionResponse, getSubstitutionRequest ] = useSubstitutionsApi("GET_SUBSTITUTION");
  const [ substitution, setSubstitution ] = React.useState<IProductSubstitution>();

  React.useEffect(() => {
    getSubstitutionRequest({ substitutionId });
  }, [ getSubstitutionRequest, substitutionId ]);

  React.useEffect(() => {
    if (getSubstitutionResponse.data) {
      setSubstitution(getSubstitutionResponse.data);
    } else if (getSubstitutionResponse.error) {
      toast.error(getSubstitutionResponse.error.message);
    }
  }, [ getSubstitutionResponse.data, getSubstitutionResponse.error ]);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    // poll if still pending
    if (getSubstitutionResponse.data?.status === ProductSubstitutionStatus.Pending) {
      timeout = setInterval(() => getSubstitutionRequest({ substitutionId }), 5000);
    }

    return () => timeout ? clearInterval(timeout) : undefined;
  }, [
    getSubstitutionRequest,
    getSubstitutionResponse.data?.status,
    substitutionId
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        {!substitution ? (
          <LoadingSpinner type="circular" />
        ) : (
          <TabbedNavigation
            tabs={[
              {
                title: "Substitution",
                name: "substitution",
                content: () => (
                  <SubstitutionTab
                    substitution={substitution}
                    {...props}
                  />
                )
              },
              {
                title: "Affected Orders",
                name: "orders",
                content: () => (
                  <AffectedOrdersTab
                    substitution={substitution}
                    {...props}
                  />
                )
              }
            ]}
          />
        )}
      </MainContentWrapper>
    </Layout>
  );
};
